import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, Trash2, Plus, ShieldCheck } from "lucide-react";

export default function Admins() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const isSuper = user?.role === "super_admin";

  const load = async () => setItems((await api.get("/admins")).data);
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post("/admins", { email, role: "admin" });
      toast.success("Administrateur ajouté");
      setEmail("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/admins/${a.id}`);
      toast.success("Administrateur retiré");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Layout title="Administrateurs" subtitle="Accès à la plateforme par adresse Google autorisée">
      <div className="space-y-5 max-w-3xl" data-testid="admins-page">
        {isSuper ? (
          <div className="card-tactical p-5">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Ajouter un administrateur</h2>
            <div className="flex flex-col sm:flex-row gap-3">
              <input data-testid="admin-email-input" type="email" placeholder="adresse@domaine.com" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2.5 text-sm mono focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
              <button onClick={add} disabled={!email} data-testid="add-admin-btn"
                className="rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-40 flex items-center justify-center gap-2">
                <Plus className="h-4 w-4" /> Ajouter
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white p-4">
            <Lock className="h-4 w-4 text-gray-500" />
            <p className="text-sm text-gray-600">Seul le super administrateur peut modifier cette liste.</p>
          </div>
        )}

        <div className="card-tactical overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Rôle</th>
                <th className="px-4 py-3">Ajouté par</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="admins-table-body">
              {items.map((a) => (
                <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-4 py-3 mono text-gray-900">{a.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                      a.role === "super_admin" ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                        : "bg-gray-100 text-gray-600 ring-gray-500/20"}`}>
                      {a.role === "super_admin" ? <ShieldCheck className="h-3 w-3" /> : null}
                      {a.role === "super_admin" ? "Super administrateur" : "Administrateur"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{a.added_by}</td>
                  <td className="px-4 py-3 mono text-xs text-gray-500">{fmtTime(a.added_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {a.role === "super_admin" ? (
                      <Lock className="h-3.5 w-3.5 text-gray-400 inline" data-testid={`admin-locked-${a.id}`} />
                    ) : isSuper ? (
                      <button onClick={() => remove(a)} data-testid={`remove-admin-${a.id}`}
                        className="rounded-md border border-gray-200 p-1.5 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
